import { useMemo } from 'react';
import { Card, Input, Select, Space, Table, Tabs, Tag, Tooltip, Typography, type TableColumnsType } from 'antd';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { api } from '../../api/client';
import { QueryError } from '../../lib/QueryState';
import { ListenerName, listenerLabel } from '../../lib/listenerName';
import { plain } from '../../lib/labels';
import { dayParam, intParam, oneOf, pageSizeParam, useUrlState } from '../../lib/urlState';
import { formatDuration } from '../listeners/listenerStats';
import { CreditSettlementButton } from '../listeners/employment/CreditSettlementButton';
import { SETTLEMENT_KIND_LABEL, SETTLEMENT_MODE_LABEL, money, todayStr, workDateLabel } from '../listeners/employment/employmentForm';
import type { ReportItem, ReportResponse, SettlementListResponse, SettlementRow } from '../listeners/employment/employmentTypes';

const VIEWS = ['employees', 'pending'] as const;
type View = (typeof VIEWS)[number];
const STATUSES = ['active', 'paused', 'ended'] as const;
const TYPES = ['full_time', 'part_time'] as const;
const MODES = ['automatic', 'manual'] as const;
const PAGE_SIZES = [25, 50, 100] as const;

/** One employment's wallet position on the chosen day, from its settlement rows. */
interface WalletDay {
  credited: bigint | null;
  pending: bigint | null;
  /** A pending row that only an admin can credit. */
  manual: boolean;
  /** The pending rows, for the inline Credit to Wallet action. */
  pendingRows: SettlementRow[];
}

/** Settlement rows of the day → per employment Σ credited / Σ pending (null when redacted). */
export function walletByEmployment(items: SettlementRow[]): Record<string, WalletDay> {
  const out: Record<string, WalletDay> = {};
  for (const s of items) {
    const e = (out[s.employmentId] ??= { credited: 0n, pending: 0n, manual: false, pendingRows: [] });
    if (s.status === 'credited') e.credited = s.amountPaise === null || e.credited === null ? null : e.credited + BigInt(s.amountPaise);
    if (s.status === 'pending') {
      e.pending = s.amountPaise === null || e.pending === null ? null : e.pending + BigInt(s.amountPaise);
      e.pendingRows.push(s);
      if (s.mode === 'manual') e.manual = true;
    }
  }
  return out;
}

const paise = (v: bigint | null): string => (v === null ? '—' : money(v.toString()));

/**
 * Admin → Employees: every salaried listener on one work date (target, worked,
 * regular, overtime, ₹ and whether the day's pay reached the wallet), and the
 * queue of settlements still waiting for a manual Credit to Wallet. The date,
 * view, filters and page live in the URL (lib/urlState), so Back and a
 * pasted link land on the same list. ₹ is "—" without the pay permission
 * (the server redacts it).
 */
export function EmployeesPage() {
  const { get, set } = useUrlState();
  const date = dayParam(get('date')) || todayStr();
  const view: View = oneOf(get('view'), VIEWS, 'employees') ?? 'employees';
  const status = oneOf(get('status'), STATUSES);
  const type = oneOf(get('type'), TYPES);
  const settlementMode = oneOf(get('settlementMode'), MODES);
  const page = intParam(get('page'), 1);
  const pageSize = pageSizeParam(get('pageSize'), PAGE_SIZES);

  const report = useQuery({
    queryKey: ['admin', 'employment', 'report', date, status ?? '', type ?? '', settlementMode ?? '', page, pageSize],
    queryFn: async () =>
      (await api.get<ReportResponse>('/admin/employment/report', { params: { from: date, to: date, status, type, settlementMode, page, pageSize } })).data,
    enabled: view === 'employees',
    placeholderData: keepPreviousData,
  });
  // The same day's settlement rows — the Wallet column. One call, up to 100 rows
  // (one or two per employee per day).
  const daySettlements = useQuery({
    queryKey: ['admin', 'employment', 'settlements', 'day', date],
    queryFn: async () => (await api.get<SettlementListResponse>('/admin/employment/settlements', { params: { from: date, to: date, pageSize: 100 } })).data,
    enabled: view === 'employees',
    placeholderData: keepPreviousData,
  });
  const pending = useQuery({
    queryKey: ['admin', 'employment', 'settlements', 'pending', page, pageSize],
    queryFn: async () => (await api.get<SettlementListResponse>('/admin/employment/settlements', { params: { status: 'pending', page, pageSize } })).data,
    enabled: view === 'pending',
    placeholderData: keepPreviousData,
  });

  const wallet = useMemo(() => walletByEmployment(daySettlements.data?.items ?? []), [daySettlements.data]);

  const employeeColumns: TableColumnsType<ReportItem> = [
    {
      title: 'Employee',
      width: 220,
      render: (_: unknown, r) => (
        <Space direction="vertical" size={0}>
          <ListenerName listener={{ displayName: r.listener.displayName, handle: r.listener.handle, id: r.listener.id }} listenerId={r.listener.id} tab="employment" />
          {r.employment.employeeCode && <Typography.Text type="secondary" style={{ fontSize: 12 }}>{r.employment.employeeCode}</Typography.Text>}
        </Space>
      ),
    },
    {
      title: 'Type',
      width: 130,
      render: (_: unknown, r) => (
        <Space size={4} wrap>
          {plain(r.employment.employmentType)}
          {r.employment.status !== 'active' && <Tag color={r.employment.status === 'paused' ? 'warning' : 'default'}>{plain(r.employment.status)}</Tag>}
        </Space>
      ),
    },
    { title: <Tooltip title="Scheduled time on this day (the daily target for a flexible schedule).">Target</Tooltip>, width: 100, render: (_: unknown, r) => formatDuration(r.scheduledSeconds) },
    { title: 'Worked', width: 100, render: (_: unknown, r) => <strong>{formatDuration(r.workedSeconds)}</strong> },
    { title: 'Regular', width: 100, render: (_: unknown, r) => formatDuration(r.regularSeconds) },
    { title: 'OT', width: 100, render: (_: unknown, r) => formatDuration(r.overtimeSeconds) },
    { title: 'Earnings', width: 120, align: 'right', render: (_: unknown, r) => <strong>{money(r.employmentTotalPaise)}</strong> },
    {
      title: <Tooltip title="Whether this day's pay has reached the listener wallet. Manual = waiting for an admin's Credit to Wallet.">Wallet</Tooltip>,
      width: 260,
      render: (_: unknown, r) => {
        const w = wallet[r.employment.id];
        if (!w) return daySettlements.isLoading ? '…' : <Typography.Text type="secondary">—</Typography.Text>;
        return (
          <Space size={4} wrap>
            {w.credited !== 0n && <Tag color="success">Credited {paise(w.credited)}</Tag>}
            {w.pending !== 0n && <Tag color="gold">Pending {paise(w.pending)}</Tag>}
            {w.manual && <Tag color="blue">Manual</Tag>}
            {w.pendingRows.map((s) => (
              <CreditSettlementButton key={s.id} settlement={s} listenerId={r.listener.id} listenerName={listenerLabel({ displayName: r.listener.displayName, handle: r.listener.handle, id: r.listener.id })} />
            ))}
          </Space>
        );
      },
    },
  ];

  const pendingColumns: TableColumnsType<SettlementRow> = [
    {
      title: 'Employee',
      width: 220,
      render: (_: unknown, r) => (
        <Space direction="vertical" size={0}>
          <ListenerName listener={{ displayName: r.listener.displayName, id: r.listener.id }} listenerId={r.listener.id} tab="employment" />
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>{plain(r.employmentType)}</Typography.Text>
        </Space>
      ),
    },
    { title: 'Work date', dataIndex: 'workDate', width: 130, render: (d: string) => workDateLabel(d) },
    { title: 'Kind', dataIndex: 'kind', width: 110, render: (k: string) => SETTLEMENT_KIND_LABEL[k as keyof typeof SETTLEMENT_KIND_LABEL] ?? plain(k) },
    { title: 'Amount', width: 120, align: 'right', render: (_: unknown, r) => <strong>{money(r.amountPaise)}</strong> },
    { title: 'Mode', dataIndex: 'mode', width: 110, render: (m: string) => <Tag color={m === 'manual' ? 'blue' : 'default'}>{SETTLEMENT_MODE_LABEL[m as keyof typeof SETTLEMENT_MODE_LABEL] ?? plain(m)}</Tag> },
    { title: 'Reason', dataIndex: 'reason', render: (r: string | null) => r ?? '—' },
    { title: 'Created', dataIndex: 'createdAt', width: 170, render: (iso: string) => new Date(iso).toLocaleString() },
    {
      title: '',
      width: 160,
      render: (_: unknown, r) => <CreditSettlementButton settlement={r} listenerId={r.listener.id} listenerName={listenerLabel({ displayName: r.listener.displayName, id: r.listener.id })} />,
    },
  ];

  const pagination = (total: number | undefined) => ({
    current: page,
    pageSize,
    total: total ?? 0,
    showSizeChanger: true,
    pageSizeOptions: PAGE_SIZES.map(String),
    showTotal: (t: number) => `${t} total`,
    onChange: (p: number, ps: number) => set({ page: p === 1 ? null : p, pageSize: ps === PAGE_SIZES[0] ? null : ps }),
  });

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div>
        <Typography.Title level={3} style={{ margin: 0 }}>
          Employees
        </Typography.Title>
        <Typography.Text type="secondary">
          Salaried listeners: what each one worked and earned on a day, and the employee pay still waiting for a wallet credit.
        </Typography.Text>
      </div>
      <Tabs
        activeKey={view}
        onChange={(k) => set({ view: k === 'employees' ? null : k, page: null })}
        items={[
          {
            key: 'employees',
            label: 'Employees',
            children: (
              <Card size="small">
                <Space wrap style={{ marginBottom: 12 }}>
                  <Input type="date" aria-label="Date" value={date} onChange={(e) => set({ date: e.target.value && e.target.value !== todayStr() ? e.target.value : null, page: null })} style={{ width: 170 }} />
                  <Select<string>
                    allowClear
                    placeholder="Any state"
                    aria-label="Employment state"
                    value={status}
                    onChange={(v) => set({ status: v || null, page: null })}
                    options={STATUSES.map((v) => ({ value: v, label: plain(v) }))}
                    style={{ minWidth: 140 }}
                  />
                  <Select<string>
                    allowClear
                    placeholder="Any type"
                    aria-label="Employment type"
                    value={type}
                    onChange={(v) => set({ type: v || null, page: null })}
                    options={TYPES.map((v) => ({ value: v, label: plain(v) }))}
                    style={{ minWidth: 140 }}
                  />
                  <Select<string>
                    allowClear
                    placeholder="Any settlement mode"
                    aria-label="Settlement mode"
                    value={settlementMode}
                    onChange={(v) => set({ settlementMode: v || null, page: null })}
                    options={MODES.map((v) => ({ value: v, label: SETTLEMENT_MODE_LABEL[v] }))}
                    style={{ minWidth: 190 }}
                  />
                  <Typography.Text type="secondary">{workDateLabel(date)}</Typography.Text>
                </Space>
                {report.isError ? (
                  <QueryError error={report.error} what="employees" onRetry={() => void report.refetch()} />
                ) : (
                  <Table<ReportItem>
                    size="small"
                    rowKey={(r) => r.employment.id}
                    loading={report.isLoading}
                    columns={employeeColumns}
                    dataSource={report.data?.items ?? []}
                    pagination={pagination(report.data?.total)}
                    scroll={{ x: 'max-content' }}
                    locale={{ emptyText: 'No employees match' }}
                  />
                )}
              </Card>
            ),
          },
          {
            key: 'pending',
            label: 'Pending wallet credits',
            children: (
              <Card size="small">
                <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
                  Every settlement not yet credited, oldest work date last. Manual rows wait for Credit to Wallet; automatic rows are picked up by the worker.
                </Typography.Paragraph>
                {pending.isError ? (
                  <QueryError error={pending.error} what="pending wallet credits" onRetry={() => void pending.refetch()} />
                ) : (
                  <Table<SettlementRow>
                    size="small"
                    rowKey="id"
                    loading={pending.isLoading}
                    columns={pendingColumns}
                    dataSource={pending.data?.items ?? []}
                    pagination={pagination(pending.data?.total)}
                    scroll={{ x: 'max-content' }}
                    locale={{ emptyText: 'Nothing waiting for a wallet credit' }}
                  />
                )}
              </Card>
            ),
          },
        ]}
      />
    </Space>
  );
}
